import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { SessionContext } from "../contexts/SessionContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import TaskItem from "../components/TaskItem";
import ProjectChip from "../components/ProjectChip";
import DynamicTabSelector from "../components/DynamicTabSelector";
import DropdownMenu from "../components/DropdownMenu";
import TopBar from "../components/TopBar";
import NotificationBell from "../components/NotificationBell";
import TrackingTray from "../components/TrackingTray";
import StatCardsRow from "../components/StatCardsRow";
import AppInfoDialog from "../components/AppInfoDialog";
import NotesPromptDialog from "../components/NotesPromptDialog";
import ScrollFadeContainer from "../components/ScrollFadeContainer";
import LabelFilterStrip from "../components/LabelFilterStrip";
import { useManageLabels } from "../hooks/useManageLabels";
import TaskSortButton from "../components/TaskSortButton";
import {
  getTaskOrderMode,
  setTaskOrderMode,
  orderTasks,
} from "../utils/taskOrderSetting";
import folderOpenIcon from "../assets/icon_folder_open.svg";
import folderAddIcon from "../assets/icon_folder_add.svg";
import accountIcon from "../assets/icon_account.svg";
import infoIcon from "../assets/icon_info.svg";
import logoutIcon from "../assets/icon_logout.svg";
import Colors from "../constants/Colors";
import { handleWipClick } from "../utils/handleWipClick";
import { handleToggleTracking } from "../utils/handleToggleTracking";
import {
  isNotesSystemEnabled,
  setNotesSystemEnabled,
  isNotesPromptShown,
  markNotesPromptShown,
  NOTES_PROMPT_TASK_THRESHOLD,
} from "../utils/notesSystemSetting";
import {
  getSelectedTaskTab,
  setSelectedTaskTab,
} from "../utils/taskTabSetting";

function HomeScreen() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const navigate = useNavigate();
  const {
    firebaseAuth,
    projects,
    tasks,
    activeProject,
    handleProjectChange,
    setTaskState,
    completeTask,
    setTaskTrackingState,
    setTaskAiTrackingState,
    createNewProject,
    createNewTask,
    updateRecordingTaskInFirestore,
    startCollectingLabels,
    labels,
  } = useContext(FirebaseContext);

  // Shared label + action-template management dialog (same on Home, Task detail
  // and Stats), replacing the per-chip pencils.
  const openManageLabels = useManageLabels(activeProject ? activeProject.id : null);
  const [selectedLabelIds, setSelectedLabelIds] = useState(new Set());
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderMode, setOrderMode] = useState(getTaskOrderMode());

  const changeOrderMode = (m) => {
    setOrderMode(m);
    setTaskOrderMode(m); // localStorage only
  };

  const toggleLabelFilter = (id) =>
    setSelectedLabelIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const { taskTypeTab, selectTaskTypeTab } = useContext(SessionContext);
  const { openModal, closeModal } = useCommonUI();
  // Notes system is opt-in (default off, like Android). Read on mount; the
  // screen remounts on navigation, so it re-reads after the Settings toggle.
  const [notesEnabled, setNotesEnabled] = useState(isNotesSystemEnabled());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load labels so task cards can render their chips.
  useEffect(() => {
    startCollectingLabels();
  }, [startCollectingLabels]);

  const handleLogout = () => {
    firebaseAuth
      .signOut()
      .then(() => {
        navigate("/auth");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
        navigate("/auth");
      });
  };

  const handleTaskClick = (task) => {
    if (task.usedAsNote) {
      navigate(`/noteDetail/${task.id}`);
    } else {
      navigate(`/taskDetail/${task.id}`);
    }
  };

  const handleCreateTaskOrNote = async () => {
    try {
      const taskId = await createNewTask(isNotesTab);
      navigate(isNotesTab ? `/noteDetail/${taskId}` : `/taskDetail/${taskId}`);
    } catch (error) {
      console.error("Error creating task or note:", error);
    }
  };

  const handleTopBarIconClick = () => {
    console.log("Folder icon clicked");
  };

  const menuItems = [
    {
      icon: folderAddIcon,
      label: "Add Project",
      onClick: async () => {
        try {
          // Create, select, and open the new project so the user can edit its
          // name/description right away.
          const newProjectId = await createNewProject();
          if (newProjectId) navigate(`/projectDetail/${newProjectId}`);
        } catch (error) {
          console.error("Error creating project:", error);
        }
      },
    },
    {
      icon: folderOpenIcon,
      label: `Open ${
        activeProject ? activeProject.projectName : "No Project Selected"
      }`,
      onClick: () => navigate(`/projectDetail/${activeProject.id}`),
    },
  ];

  // Profile menu, mirroring the Android account menu's signed-in tail:
  // App Information (opens a dialog) then Sign Out (danger color).
  const accountMenuItems = [
    {
      icon: infoIcon,
      label: "App Information",
      onClick: () => openModal(<AppInfoDialog />),
    },
    {
      icon: logoutIcon,
      label: "Sign Out",
      color: "#ff5a5a",
      onClick: handleLogout,
    },
  ];

  // Type-based tabs so the index-shift from hiding "Notes" can't desync the
  // filter. The Notes tab only appears when the notes system is enabled.
  const ALL_TAB_DEFS = [
    { label: "All", type: "all" },
    { label: "Notes", type: "notes" },
    { label: "Todos", type: "backlog" },
    { label: "WIP", type: "wip" },
    { label: "Done", type: "done" },
  ];
  const tabDefs = ALL_TAB_DEFS.filter(
    (t) => t.type !== "notes" || notesEnabled
  );
  const tabs = tabDefs.map((t) => t.label);
  const safeTabIndex = Math.min(taskTypeTab, tabDefs.length - 1);
  const activeTabType = tabDefs[safeTabIndex]?.type ?? "all";
  const isNotesTab = activeTabType === "notes";

  const handleTabSelection = (selectedIndex) => {
    selectTaskTypeTab(selectedIndex);
    // Persist the TYPE (not the index), so the choice survives a reload even
    // after the Notes tab is hidden/shown (which shifts indices).
    const type = tabDefs[selectedIndex]?.type;
    if (type) setSelectedTaskTab(type);
  };

  // Restore the persisted tab on mount. If the saved tab is "notes" but the
  // notes system is off, fall back to "All" AND rewrite the bad persisted value
  // to "all" so it doesn't keep resolving to the wrong tab.
  const didRestoreTab = useRef(false);
  useEffect(() => {
    if (didRestoreTab.current) return;
    didRestoreTab.current = true;
    let type = getSelectedTaskTab();
    if (type === "notes" && !notesEnabled) {
      type = "all";
      setSelectedTaskTab("all");
    }
    const idx = tabDefs.findIndex((t) => t.type === type);
    if (idx >= 0 && idx !== taskTypeTab) selectTaskTypeTab(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTerm = searchActive ? searchQuery.trim().toLowerCase() : "";
  // Memoized: during boot, the FirebaseContext fires a burst of unrelated
  // updates (labels, rules, notifications, invoices, ...) — without useMemo
  // each one re-filtered and re-sorted the whole task list, starving the
  // marquee animation on the cards and making the first seconds feel laggy.
  const orderedTasks = useMemo(() => {
    const filteredTasks = tasks.filter((task) => {
      // Title search (case-insensitive substring), like Android's TaskListVisibility.
      if (searchTerm && !(task.title || "").toLowerCase().includes(searchTerm)) {
        return false;
      }
      // Label filter (AND — task must have every selected label, like Android).
      if (selectedLabelIds.size > 0) {
        const ids = (task.labelIds || []).map(Number);
        for (const sel of selectedLabelIds) {
          if (!ids.includes(sel)) return false;
        }
      }
      switch (activeTabType) {
        case "notes":
          return task.usedAsNote === true;
        case "backlog":
          return !task.usedAsNote && task.taskState === "task_state_backlog";
        case "wip":
          return !task.usedAsNote && task.taskState === "task_state_wip";
        case "done":
          return !task.usedAsNote && task.taskState === "task_state_done";
        default:
          // "All" shows tasks only — notes live in their own tab.
          return !task.usedAsNote;
      }
    });
    // Apply the selected sort order (mirrors Android's orderTasks).
    return orderTasks(filteredTasks, orderMode);
  }, [tasks, searchTerm, selectedLabelIds, activeTabType, orderMode]);

  // One-shot prompt: once the user has NOTES_PROMPT_TASK_THRESHOLD non-note
  // tasks, offer to turn on the notes system (mirrors Android).
  useEffect(() => {
    if (notesEnabled || isNotesPromptShown()) return;
    const nonNoteCount = tasks.filter((t) => !t.usedAsNote).length;
    if (nonNoteCount < NOTES_PROMPT_TASK_THRESHOLD) return;
    markNotesPromptShown();
    openModal(
      <NotesPromptDialog
        onLater={closeModal}
        onTurnOn={() => {
          setNotesSystemEnabled(true);
          setNotesEnabled(true);
          selectTaskTypeTab(1); // jump to the freshly-enabled Notes tab
          setSelectedTaskTab("notes");
          closeModal();
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, notesEnabled]);

  const styles = {
    container: {
      // 50px top bar + 10px gap + 40px tab selector = 100px; the body's own
      // 16px top padding then adds the gap below the tabs (before the tray).
      paddingTop: "100px",
      display: "flex",
      flexDirection: "column",
      // Fill the viewport minus the fixed 60px bottom bar; the body below
      // (flex column) then lets the task list grow into all remaining space.
      height: "calc(100vh - 60px)",
      boxSizing: "border-box",
    },
    // The body between the fixed top bar and the bottom bar: a flex column so
    // the task list can grow to fill all remaining vertical space. No bottom
    // padding — the list runs to the bottom bar (with the edge fade).
    body: {
      padding: "16px 16px 0",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    },
    projectSelectorContainer: {
      marginTop: "16px",
    },
    scrollableTaskList: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      // No top margin — the gap above the list comes solely from the tray's
      // (or the body's) 16px, so it's a single uniform 16px, not doubled.
      paddingBottom: "16px", // keep the last card clear of the bottom bar
    },
    fabButton: {
      position: "fixed",
      bottom: "72px",
      right: "16px",
      backgroundColor: isNotesTab ? Colors.secondary : Colors.primary,
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: "56px",
      height: "56px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "24px",
      cursor: "pointer",
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      zIndex: 1000,
    },
  };

  return (
    <div id="home-screen" style={styles.container}>
      <TopBar
        leftContainer={
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <DropdownMenu icon={accountIcon} menuItems={accountMenuItems} />
            <NotificationBell />
          </div>
        }
        onClick={handleTopBarIconClick}
        rightContainer={
          <ProjectChip
            projects={projects}
            activeProject={activeProject}
            onProjectChange={handleProjectChange}
            actionItems={menuItems}
          />
        }
      />
      <div style={styles.body}>
        <DynamicTabSelector
          tabs={tabs}
          selectedTabIndex={taskTypeTab}
          onTabSelected={handleTabSelection}
          style={{ top: "60px", left: "16px", right: "16px", width: "auto" }}
        />
        <TrackingTray />
        {/* Four swappable stat cards for the active project (scoped to its full
            task set, independent of the tab/label/search filters). Sits above
            the label/search circle buttons, like Android's stat strip. */}
        <StatCardsRow tasks={tasks} />
        <LabelFilterStrip
          labels={labels}
          selectedIds={selectedLabelIds}
          onToggle={toggleLabelFilter}
          onClearAll={() => setSelectedLabelIds(new Set())}
          onAddLabel={openManageLabels}
          showSearchButton
          isSearchActive={searchActive}
          searchQuery={searchQuery}
          onSearchToggle={(v) => {
            setSearchActive(v);
            if (!v) setSearchQuery("");
          }}
          onSearchQueryChange={setSearchQuery}
          trailing={
            <TaskSortButton
              currentMode={orderMode}
              onSelect={changeOrderMode}
              excludeDueDate={isNotesTab}
            />
          }
        />
        <ScrollFadeContainer style={styles.scrollableTaskList}>
          {orderedTasks.length > 0 ? (
            <div className="task-grid">
              {orderedTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  name={task.title}
                  description={task.description}
                  task={task}
                  labels={labels}
                  onClick={() => handleTaskClick(task)}
                  onToggleDone={() =>
                    task.taskState === "task_state_done"
                      ? setTaskState(task.id, "task_state_backlog")
                      : completeTask(task)
                  }
                  onWipClick={() => handleWipClick(task, setTaskState)}
                  onPlayClick={() =>
                    handleToggleTracking(
                      task,
                      setTaskTrackingState,
                      updateRecordingTaskInFirestore
                    )
                  }
                  onStopClick={() =>
                    handleToggleTracking(
                      task,
                      setTaskTrackingState,
                      updateRecordingTaskInFirestore
                    )
                  }
                  onAiPlayClick={() =>
                    setTaskAiTrackingState(task.id, task.projectId, true)
                  }
                  onAiStopClick={() =>
                    setTaskAiTrackingState(task.id, task.projectId, false)
                  }
                />
              ))}
            </div>
          ) : (
            <div>No tasks found</div>
          )}
        </ScrollFadeContainer>
      </div>
      <button onClick={handleCreateTaskOrNote} style={styles.fabButton}>
        +
      </button>
    </div>
  );
}

export default HomeScreen;
